/**
 * @module Actions.SubscriptionPlan
 *
 */
import { Dispatch } from "react";
import { action, ActionType } from "typesafe-actions";
import { RequestError } from "typedefs";
import { SubscriptionModel } from "app/models";
import { fetchGet, fetchPost } from "app/utils/request";
import { AnalyticsService } from "app/services";
import { getClickIdParams } from "app/utils/getClickIdParams";

const pending = () => action("@subscription.fetch.pending");
const error = (error: RequestError) =>
  action("@subscription.fetch.error", error);
const success = (data: SubscriptionModel) =>
  action("@subscription.fetch.success", data);

export const clearSubscription = () => action("@subscription.fetch.clear");

const actionHandlers = { pending, error, success, clearSubscription };

export type SubscriptionAction = ActionType<typeof actionHandlers>;

export const startTrial = () => {
  return (dispatch: Dispatch<SubscriptionAction>) => {
    dispatch(pending());

    return fetchPost("subscriptions/trial", getClickIdParams(), { version: 1 })
      .then((data: { subscriptions: SubscriptionModel[] }) => {
        AnalyticsService.track("Trial Started", { Source: "Web" });
        dispatch(success(data.subscriptions[0]));
      })
      .catch((err) => dispatch(error(err)));
  };
};

export const updateRenewal = ({
  product,
  enable,
}: {
  product: SubscriptionModel;
  enable: boolean;
}) => {
  return async (dispatch: Dispatch<SubscriptionAction>) => {
    try {
      await fetchPost(
        "stripe/update_renewal",
        { product, enable },
        { version: 1 },
      );
    } catch (err) {
      dispatch(error(err));
      // Surface the failure to the caller — the subscriptions page must not
      // report success (toast + redirect) for a refused renewal toggle.
      throw err;
    }
  };
};

export const cancelProTrialSubscription = () => {
  return async (dispatch: Dispatch<SubscriptionAction>) => {
    try {
      await fetchPost("stripe/cancel", {}, { version: 1 });
    } catch (err) {
      dispatch(error(err));
      // Surface the failure to the caller — the confirm modal must not
      // report success (toast + redirect) for a failed cancellation.
      throw err;
    }
  };
};

export const fetchSubscriptions = () => {
  return (dispatch: Dispatch<SubscriptionAction>) => {
    dispatch(pending());

    return fetchGet("account/paid_subscriptions", {}, { version: 1 })
      .then((data) => dispatch(success(data)))
      .catch((err) => dispatch(error(err)));
  };
};

export const actions = {
  startTrial,
};
